import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, Sun, Moon, PackageSearch, LogOut, Shield } from "lucide-react";
import { NAV } from "../../data/content";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../context/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { Wordmark } from "./Logo";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";

export default function Navbar() {
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (loc.pathname !== "/") { nav("/"); setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }), 300); }
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };
  const key = (href) => href.replace("#", "");
  const isAdmin = user?.role === "admin";
  const onHero = !scrolled;
  const iconBtnCls = onHero ? "border-white/25 text-white hover:bg-white/10" : "border-border hover:bg-secondary";

  return (
    <header data-testid="navbar" className={`fixed top-0 inset-x-0 z-[60] transition-all duration-500 ${scrolled ? "py-3" : "py-5"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <nav className={`flex items-center justify-between rounded-full px-5 py-3 transition-all duration-500 ${scrolled ? "glass shadow-lg" : ""}`}>
          <button data-testid="nav-logo" onClick={() => go("#home")} className={`flex items-center gap-2 font-heading font-extrabold tracking-tight text-lg transition-colors ${onHero ? "text-white" : "text-foreground"}`}>
            <Wordmark variant={onHero ? "dark" : "auto"} sizeClass="h-7" />
          </button>

          <div className="flex items-center gap-2">
            <LanguageSwitcher onHero={onHero} />
            <button data-testid="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${iconBtnCls}`}>
              <Sun className="w-4 h-4 hidden dark:block" /><Moon className="w-4 h-4 block dark:hidden" />
            </button>
            {isAdmin && (
              <button data-testid="nav-admin" onClick={() => nav("/admin")}
                className={`hidden sm:inline-flex items-center gap-2 rounded-full border text-sm font-semibold px-4 py-2.5 transition-colors ${iconBtnCls}`}>
                <Shield className="w-4 h-4" /> {t("actions.admin")}
              </button>
            )}
            <button data-testid="nav-track" onClick={() => nav("/track")}
              className={`hidden sm:inline-flex items-center gap-2 rounded-full border text-sm font-semibold px-4 py-2.5 transition-colors ${iconBtnCls}`}>
              <PackageSearch className="w-4 h-4" /> {t("actions.track")}
            </button>
            {user && (
              <button data-testid="nav-logout" onClick={async () => { await logout(); nav("/"); }}
                className="hidden sm:inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold px-5 py-2.5 hover:opacity-90 transition-opacity">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            )}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${iconBtnCls}`}><Menu className="w-4 h-4" /></button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-border">
                <div className="flex flex-col gap-5 mt-12">
                  {NAV.map((n) => (
                    <button key={n.href} data-testid={`mobile-nav-${key(n.href)}`} onClick={() => go(n.href)} className="text-left text-2xl font-heading font-bold">{t(`nav.${key(n.href)}`)}</button>
                  ))}
                  <button data-testid="mobile-nav-insights" onClick={() => { setOpen(false); nav("/blog"); }} className="text-left text-2xl font-heading font-bold">{t("nav.insights")}</button>
                  <button data-testid="mobile-nav-track" onClick={() => { setOpen(false); nav("/track"); }} className="text-left text-2xl font-heading font-bold text-primary">{t("actions.trackShipment")}</button>
                  {isAdmin && <button data-testid="mobile-nav-admin" onClick={() => { setOpen(false); nav("/admin"); }} className="text-left text-2xl font-heading font-bold">{t("actions.admin")}</button>}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
}
